import { useState, useEffect } from "react";
import ProviderLayout from "@/layouts/ProviderLayout";
import { useSearchAgents, useConnectAgent, useDisconnectAgent, useGetMe } from "@/lib/api-client-react";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { Briefcase, MapPin, Search, Star, ShieldCheck, Mail, Phone, FileText, BadgeCheck, Plus, Users } from "lucide-react";
import { Input } from "@/components/ui/input";
import { motion, Variants } from "framer-motion";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

const fadeUp: Variants = {
  hidden: { opacity: 0, y: 20 },
  visible: (i: number) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.1, duration: 0.5, ease: "easeOut" },
  }),
};

function AgentListItem({ a, isConnected, index, onConnect, onDisconnect, isPending, inPopup = false }: any) {
  const [showDetails, setShowDetails] = useState(!inPopup);

  return (
    <motion.div
      custom={index}
      initial="hidden"
      animate="visible"
      variants={fadeUp}
      className={`group relative bg-white/60 dark:bg-card/60 backdrop-blur-xl rounded-2xl border ${isConnected ? 'border-primary/20' : 'border-white/20 dark:border-white/10'} p-5 sm:p-6 flex flex-col sm:flex-row sm:items-start justify-between gap-6 shadow-[0_8px_30px_rgb(0,0,0,0.04)] hover:shadow-[0_8px_30px_rgb(0,0,0,0.08)] ${!isConnected && 'hover:border-primary/30'} transition-all overflow-hidden`}
    >
      <div className="absolute inset-0 bg-gradient-to-r from-primary/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />

      <div className="relative z-10 flex flex-col sm:flex-row sm:items-start gap-5 flex-1">
        <div className="w-14 h-14 rounded-full bg-gradient-to-br from-primary/20 to-primary/5 flex items-center justify-center text-primary border border-primary/10 shrink-0 mt-1">
          <ShieldCheck className="w-7 h-7" />
        </div>

        <div className="flex-1 space-y-3">
          <div>
            <div className="flex flex-wrap items-center gap-3 mb-1">
              <h3 className={`font-bold text-xl text-foreground ${!isConnected && 'group-hover:text-primary'} transition-colors`}>{a.name}</h3>
              {isConnected && (
                <span className="px-2.5 py-0.5 rounded-full bg-primary/10 text-primary text-xs font-semibold flex items-center gap-1">
                  <Star className="w-3 h-3 fill-current" />
                  Connected
                </span>
              )}
              {a.agentType && (
                <span className="px-2.5 py-0.5 rounded-full bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300 text-xs font-semibold border border-slate-200 dark:border-slate-700">
                  {a.agentType}
                </span>
              )}
            </div>
            <p className="text-muted-foreground text-sm flex items-center gap-1.5 font-medium">
              <Briefcase className="w-4 h-4 text-primary/70" />
              {a.agencyName || "Independent Agent"}
            </p>
          </div>

          {showDetails && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-y-2 gap-x-4 text-sm pt-2 border-t border-border/50">
              {a.email && (
                <div className="flex items-center gap-2 text-muted-foreground">
                  <Mail className="w-4 h-4 text-primary/60 shrink-0" />
                  <span className="truncate">{a.email}</span>
                </div>
              )}
              {a.phone && (
                <div className="flex items-center gap-2 text-muted-foreground">
                  <Phone className="w-4 h-4 text-primary/60 shrink-0" />
                  <span>{a.phone}</span>
                </div>
              )}
              {a.npnNumber && (
                <div className="flex items-center gap-2 text-muted-foreground">
                  <BadgeCheck className="w-4 h-4 text-primary/60 shrink-0" />
                  <span>NPN: {a.npnNumber}</span>
                </div>
              )}
              {a.licenseNumber && (
                <div className="flex items-center gap-2 text-muted-foreground">
                  <FileText className="w-4 h-4 text-primary/60 shrink-0" />
                  <span>Lic: {a.licenseNumber}</span>
                </div>
              )}
              {a.experience && (
                <div className="flex items-center gap-2 text-muted-foreground">
                  <Star className="w-4 h-4 text-primary/60 shrink-0" />
                  <span>{a.experience} yrs exp</span>
                </div>
              )}
              {a.licensedStates && a.licensedStates.length > 0 && (
                <div className="flex items-center gap-2 text-muted-foreground">
                  <MapPin className="w-4 h-4 text-primary/60 shrink-0" />
                  <span>{a.licensedStates.length} licensed state{a.licensedStates.length > 1 ? 's' : ''}</span>
                </div>
              )}
              {a.connectedAgents && a.connectedAgents.length > 0 && (
                <div className="col-span-full pt-3 mt-1 border-t border-border/40">
                  <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2 flex items-center gap-1.5">
                    <Users className="w-3.5 h-3.5 text-primary" /> Connected Team Members ({a.connectedAgents.length})
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {a.connectedAgents.map((ca: any) => (
                      <div key={ca.id || ca.email} className="flex items-center gap-1.5 bg-slate-50 dark:bg-slate-800/60 border border-slate-200 dark:border-slate-700/60 rounded-xl px-2.5 py-1 text-xs">
                        <span className="font-semibold text-slate-800 dark:text-slate-200">{ca.name}</span>
                        <span className="text-slate-500 text-[11px]">({ca.email})</span>
                        {ca.status && (
                          <span className={`px-1.5 py-0.2 rounded-full text-[10px] font-semibold ${ca.status === 'ACCEPTED' ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300' : 'bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-300'}`}>
                            {ca.status}
                          </span>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {inPopup && (
            <div>
              <button onClick={() => setShowDetails(!showDetails)} className="text-sm text-primary hover:underline font-medium">
                {showDetails ? "Hide details" : "View details"}
              </button>
            </div>
          )}
        </div>
      </div>

      <div className="relative z-10 shrink-0 sm:pt-2">
        {isConnected ? (
          <Button
            variant="destructive"
            className="w-full sm:w-auto rounded-xl h-10 font-semibold transition-all shadow-md"
            onClick={() => onDisconnect(a.id)}
            disabled={isPending}
          >
            Disconnect
          </Button>
        ) : (
          <Button
            variant="default"
            className="w-full sm:w-auto rounded-xl h-10 font-semibold transition-all shadow-md shadow-primary/25 hover:shadow-primary/40"
            onClick={() => onConnect(a.id)}
            disabled={isPending}
          >
            Connect
          </Button>
        )}
      </div>
    </motion.div>
  );
}

export default function ProviderAgents() {
  const [user, setUser] = useState<any>(null);
  useEffect(() => {
    const u = localStorage.getItem("user");
    if (u) {
      try { setUser(JSON.parse(u)); } catch (e) { }
    }
  }, []);

  const { data: meUser } = useGetMe();
  const { data: agents, isLoading: loadingAgents } = useSearchAgents();
  const connectAgent = useConnectAgent();
  const disconnectAgent = useDisconnectAgent();
  const [searchQuery, setSearchQuery] = useState("");
  const [isPopupOpen, setIsPopupOpen] = useState(false);

  const effectiveUser = meUser || user;
  const connectedAgentId = effectiveUser?.provider?.agentId || effectiveUser?.member?.agentId || effectiveUser?.agentId || user?.provider?.agentId || user?.member?.agentId || user?.agentId;
  let connectedAgent = agents?.find((a: any) => a.id === connectedAgentId || a.userId === connectedAgentId);

  if (!connectedAgent && (effectiveUser?.provider?.agent || effectiveUser?.member?.agent)) {
    const ag = effectiveUser?.provider?.agent || effectiveUser?.member?.agent;
    if (ag) {
      connectedAgent = {
        id: ag.id,
        userId: ag.userId,
        name: ag.user?.displayName || `${ag.user?.firstName || ''} ${ag.user?.lastName || ''}`.trim() || ag.agencyName || "Connected Agent",
        email: ag.user?.email,
        phone: ag.user?.phone,
        agencyName: ag.agencyName,
        experience: ag.experience,
        licensedStates: ag.licensedStates,
        agentType: ag.agentType,
        npnNumber: ag.npnNumber,
        licenseNumber: ag.licenseNumber,
      };
    }
  }

  const filteredAgents = agents?.filter((a: any) => {
    if (connectedAgent && (a.id === connectedAgent.id || a.userId === connectedAgent.userId)) return false;
    return a.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      a.agencyName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      a.email?.toLowerCase().includes(searchQuery.toLowerCase());
  }) || [];

  const handleConnect = (agentId: string) => {
    connectAgent.mutate(agentId, {
      onSuccess: () => {
        const u = localStorage.getItem("user");
        if (u) {
          try {
            const parsed = JSON.parse(u);
            if (parsed.provider) parsed.provider.agentId = agentId;
            localStorage.setItem("user", JSON.stringify(parsed));
            setUser(parsed);
          } catch (e) { }
        }
        setIsPopupOpen(false);
      }
    });
  };

  const handleDisconnect = (agentId: string) => {
    disconnectAgent.mutate(agentId, {
      onSuccess: () => {
        const u = localStorage.getItem("user");
        if (u) {
          try {
            const parsed = JSON.parse(u);
            if (parsed.provider) parsed.provider.agentId = null;
            localStorage.setItem("user", JSON.stringify(parsed));
            setUser(parsed);
          } catch (e) { }
        }
      }
    });
  };

  return (
    <ProviderLayout>
      <div className="w-full p-6 sm:p-8 lg:p-10 xl:px-12 sm:lg:xl:space-y-10 relative">
        <div className="absolute top-0 -left-10 w-72 h-72 bg-primary/10 rounded-full blur-3xl -z-10 pointer-events-none" />
        <div className="absolute top-40 -right-10 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl -z-10 pointer-events-none" />

        <motion.div custom={0} initial="hidden" animate="visible" variants={fadeUp} className="flex flex-col md:flex-row md:items-center justify-between gap-6">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-primary/10 rounded-xl flex-shrink-0">
              <ShieldCheck className="w-6 h-6 text-primary" />
            </div>
            <div>
              <h1 className="text-4xl font-extrabold tracking-tight text-foreground mb-2">My Agent</h1>
              <p className="text-lg text-muted-foreground max-w-xl">
                Manage your connected licensed agent to expand your network and navigate care seamlessly.
              </p>
            </div>
          </div>
        </motion.div>

        <div className="space-y-4 mt-8">
          {loadingAgents ? (
            Array.from({ length: 1 }).map((_, i) => (
              <Skeleton key={i} className="w-full h-32 rounded-2xl" />
            ))
          ) : connectedAgentId && connectedAgent ? (
            <AgentListItem
              a={connectedAgent}
              isConnected={true}
              index={1}
              onConnect={handleConnect}
              onDisconnect={handleDisconnect}
              isPending={disconnectAgent.isPending}
              inPopup={false}
            />
          ) : (
            <motion.div custom={1} initial="hidden" animate="visible" variants={fadeUp} className="flex flex-col items-center justify-center py-16 text-center bg-white/50 dark:bg-card/50 backdrop-blur-sm rounded-3xl border border-dashed border-border hover:border-primary transition-colors">
              <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mb-4">
                <ShieldCheck className="w-8 h-8 text-primary/50" />
              </div>
              <h3 className="text-xl font-bold mb-2">No Agent Connected</h3>
              <p className="text-muted-foreground max-w-sm mb-6">You don't have an agent right now. Click below to add an agent.</p>
              <Button onClick={() => setIsPopupOpen(true)} className="rounded-xl h-12 px-6 gap-2 font-semibold text-white shadow-lg">
                <Plus className="w-5 h-5" /> Add Agent
              </Button>
            </motion.div>
          )}
        </div>
      </div>

      <Dialog open={isPopupOpen} onOpenChange={setIsPopupOpen}>
        <DialogContent className="sm:max-w-4xl h-[90vh] flex flex-col p-0 overflow-hidden">
          <DialogHeader className="p-6 pb-4">
            <DialogTitle className="text-2xl font-bold">Available Agents</DialogTitle>
            <div className="relative w-full mt-4 shadow-sm rounded-xl overflow-hidden">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Search className="w-5 h-5 text-primary/70" />
              </div>
              <Input
                placeholder="Search by name or agency..."
                className="pl-10 h-12 border-border/50 text-base focus-visible:ring-primary"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
          </DialogHeader>
          <div className="flex-1 overflow-y-auto p-6 pt-2 space-y-4">
            {filteredAgents.length > 0 ? (
              filteredAgents.map((a: any, index: number) => (
                <AgentListItem
                  key={a.id}
                  a={a}
                  isConnected={false}
                  index={index + 1}
                  onConnect={handleConnect}
                  onDisconnect={handleDisconnect}
                  isPending={connectAgent.isPending}
                  inPopup={true}
                />
              ))
            ) : (
              <div className="flex flex-col items-center justify-center py-10 text-center">
                <Search className="w-8 h-8 text-muted-foreground/50 mb-3" />
                <h3 className="text-lg font-semibold text-muted-foreground">No agents found</h3>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </ProviderLayout>
  );
}
